import { css, cva } from '@styled-system/css';
import { token } from '@styled-system/tokens';
import type { SystemProperties } from '@styled-system/types';
import type { ReactNode } from 'react';

export type TagKind =
  | 'neutral'
  | 'category'
  | 'brand'
  | 'size'
  | 'condition'
  | 'conditionExcellent'
  | 'conditionGood'
  | 'conditionFair'
  | 'conditionPoor';

const tagRecipe = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2',
    px: '3',
    py: '1',
    borderRadius: 'xs',
    borderWidth: '1px',
    borderStyle: 'solid',
    fontFamily: 'sans',
    fontSize: 'caption',
    fontWeight: 'medium',
    lineHeight: 'caption',
    whiteSpace: 'nowrap',
  },
  variants: {
    kind: {
      neutral: {
        bg: 'surface.sunken',
        color: 'text.secondary',
        borderColor: 'border.default',
      },
      category: {
        bg: 'surface.sunken',
        color: 'text.secondary',
        borderColor: 'border.default',
      },
      brand: {
        bg: 'brand.primarySoft',
        color: 'text.brand',
        borderColor: 'brand.primarySoft',
      },
      size: {
        bg: 'warning.soft',
        color: 'warning.default',
        borderColor: 'warning.border',
      },
      // Fallback générique (rétro-compat) — préférer les variantes par tier
      // ci-dessous (resolved via `getConditionTier(label)`).
      condition: {
        bg: 'success.soft',
        color: 'success.default',
        borderColor: 'success.border',
      },
      // Neuf avec/sans étiquette → vert vif (success).
      conditionExcellent: {
        bg: 'success.soft',
        color: 'success.default',
        borderColor: 'success.border',
      },
      // Très bon état → teal/emerald : encore "fiable" mais distinct du neuf.
      // Pas de token semantic dédié → tinted via inline color-mix sur teal-600.
      conditionGood: {
        bg: 'color-mix(in srgb, #0D9488 14%, transparent)',
        color: '#0D9488',
        borderColor: 'color-mix(in srgb, #0D9488 28%, transparent)',
      },
      // Bon état → orange (warning) : usé mais utilisable.
      conditionFair: {
        bg: 'warning.soft',
        color: 'warning.default',
        borderColor: 'warning.border',
      },
      // Satisfaisant / pièces cassées → rouge (error) : alerte.
      conditionPoor: {
        bg: 'error.soft',
        color: 'error.default',
        borderColor: 'error.border',
      },
    },
  },
  defaultVariants: { kind: 'neutral' },
});

interface TagProps {
  children: ReactNode;
  kind?: TagKind;
  /**
   * Legacy: tinted variant where the bg is a 10% mix of the given color.
   * Prefer `kind` for new code. When `color` is provided it overrides `kind`.
   */
  color?: SystemProperties['color'];
  className?: string;
}

export const Tag = ({ children, kind = 'neutral', color, className }: TagProps) => {
  if (color) {
    const resolved =
      typeof color === 'string' && color.includes('.')
        ? token.var(`colors.${color}` as Parameters<typeof token.var>[0])
        : (color as string);
    const legacy = css({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: '3',
      py: '1',
      borderRadius: 'full',
      fontFamily: 'sans',
      fontSize: 'caption',
      fontWeight: 'semibold',
      whiteSpace: 'nowrap',
      lineHeight: 'caption',
    });
    return (
      <span
        className={`${legacy} ${className ?? ''}`}
        style={{
          color: resolved,
          backgroundColor: `color-mix(in srgb, ${resolved} 12%, transparent)`,
        }}
      >
        {children}
      </span>
    );
  }
  return <span className={`${tagRecipe({ kind })} ${className ?? ''}`}>{children}</span>;
};
